import { ApiError, FramesService, type FrameResponseDto, type CancelablePromise } from "@/api";
import { AspectRatio, type FrameModel } from "@/lib/model/Frame";
import { prefetchImages } from "@/lib/utils/imagePrefetch";
import { useQuery } from "@tanstack/react-query";
import { useEffect } from "react";

const mapAspectRatio = (aspectRatio: string): AspectRatio => {
    switch (aspectRatio) {
        case 'ONE_TO_ONE':
            return AspectRatio.ONE_TO_ONE;
        case 'FOUR_TO_THREE':
            return AspectRatio.FOUR_TO_THREE;
        case 'THREE_TO_FOUR':
            return AspectRatio.THREE_TO_FOUR;
        case 'FOUR_TO_FIVE':
            return AspectRatio.FOUR_TO_FIVE;
        case 'FIVE_TO_FOUR':
            return AspectRatio.FIVE_TO_FOUR;
        default:
            return AspectRatio.ONE_TO_ONE;
    }
}

export const mapFrameResponseDtoToFrameModel = (data: FrameResponseDto[]): FrameModel[] =>
    data.flatMap((parentFrame) =>
        parentFrame.childFrames.map((childFrame) => ({
            childFrameId: childFrame.id,
            name: childFrame.name,
            description: childFrame.description,
            sampleImageUrl: childFrame.sampleImageUrl,
            imagesCount: parentFrame.imagesCount,
            frameDetailSampleImageUrl: childFrame.frameDetailSampleImageUrl,
            layout: parentFrame.layout,
            price: childFrame.price,
            aspectRatio: mapAspectRatio(childFrame.aspectRatio),
            isCollabFrame: childFrame.isCollabFrame,
        }))
    );

export const framesQueryKey = ["frames"] as const;

export const FRAMES_STALE_TIME = 1000 * 60 * 60 * 24; // 24 hours

export const fetchFrames = (): CancelablePromise<FrameResponseDto[]> =>
    FramesService.framesControllerGetFrames();

export const extractFrameImageUrls = (frames: FrameModel[]): string[] =>
    frames.flatMap((f) => [f.frameDetailSampleImageUrl, f.sampleImageUrl]);

export const useGetFrames = () => {
    const result = useQuery({
        queryKey: framesQueryKey,
        queryFn: fetchFrames,
        retry: (failureCount, error) => {
            if (error instanceof ApiError) {
                return error.status === 500 && failureCount < 3;
            }
            return failureCount < 3;
        },
        select: mapFrameResponseDtoToFrameModel,
        staleTime: FRAMES_STALE_TIME,
        gcTime: FRAMES_STALE_TIME,
        refetchOnWindowFocus: false,
        refetchOnMount: false,
        refetchOnReconnect: false,
        refetchInterval: false,
        refetchIntervalInBackground: false,
    })

    useEffect(() => {
        if (result.data) {
            prefetchImages(extractFrameImageUrls(result.data));
        }
    }, [result.data]);

    return result;
}